import { del } from 'utils/http/del';
import { postJson } from 'utils/http/postJson';
import { putJson } from 'utils/http/putJson';

type SaveEndpointUrlFactory<TPayload, TUrl extends string> = (
  payload: TPayload,
) => TUrl;

type SaveEndpointPayloadMapper<
  TMappedPayload extends Record<string, unknown>,
  TPayload,
> = (payload: TPayload) => TMappedPayload;

type SaveEndpointResponseMapper<TMappedResponse, TResponse> = (
  response: TResponse,
) => TMappedResponse;

const httpFns = {
  put: putJson,
  post: postJson,
  del,
};

export class SaveEndpoint<
  TUrl extends string,
  TMappedResponse,
  TResponse,
  TMappedPayload extends Record<string, unknown>,
  TPayload,
  TMethod extends 'put' | 'post' | 'del',
> {
  urlFactory: SaveEndpointUrlFactory<TPayload, TUrl>;

  mapPayload: SaveEndpointPayloadMapper<TMappedPayload, TPayload>;

  mapResponse: SaveEndpointResponseMapper<TMappedResponse, TResponse>;

  method: TMethod;

  constructor(args: {
    urlFactory: SaveEndpointUrlFactory<TPayload, TUrl>;
    mapPayload: SaveEndpointPayloadMapper<TMappedPayload, TPayload>;
    mapResponse: SaveEndpointResponseMapper<TMappedResponse, TResponse>;
    method: TMethod;
  }) {
    this.urlFactory = args.urlFactory;
    this.mapPayload = args.mapPayload;
    this.mapResponse = args.mapResponse;
    this.method = args.method;
  }

  url(payload: TPayload): TUrl {
    return this.urlFactory(payload);
  }

  createSaveFn() {
    return async (payload: TPayload): Promise<TMappedResponse> => {
      const url = this.urlFactory(payload);
      const data = this.mapPayload(payload);

      const response = await httpFns[this.method]<TResponse>(url, data);

      return this.mapResponse(response);
    };
  }
}
